import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  MessageSquare,
  RefreshCw,
  Search,
  Phone,
  Mail,
  Calendar,
  User,
  Building,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Eye,
  Send,
  MessageCircle,
  Package,
} from "lucide-react";

export default function WhatsAppTicketsPage() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageInput, setPageInput] = useState("");
  const [stats, setStats] = useState({ pending: 0, resolved: 0, total: 0 });
  
  // Detail modal
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const pageSize = 20;

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * pageSize,
        limit: pageSize,
      });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchQuery) params.append("search", searchQuery);

      const response = await axios.get(
        `${API_URL}/whatsapp/tickets?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTickets(response.data.tickets || []);
      setTotalTickets(response.data.total || 0);
      setStats(response.data.stats || { pending: 0, resolved: 0, total: 0 });
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter, searchQuery]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Poll the list every 15s so tickets with new customer/staff activity bubble
  // to the top and the pending count stays fresh (live inbox feel).
  useEffect(() => {
    const id = setInterval(() => { fetchTickets(); }, 15000);
    return () => clearInterval(id);
  }, [fetchTickets]);

  // Live-refresh the OPEN conversation every 15s (unless it's resolved/closed).
  useEffect(() => {
    if (!showDetailModal || !selectedTicket?.ticket_id) return;
    if (["resolved", "closed"].includes(selectedTicket.status)) return;
    const id = setInterval(() => { fetchTicketMessages(selectedTicket); }, 15000);
    return () => clearInterval(id);
  }, [showDetailModal, selectedTicket?.ticket_id, selectedTicket?.status]);

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage("");
    setTicketMessages([]);
    setShowDetailModal(true);
    fetchTicketMessages(ticket);
  };

  const fetchTicketMessages = async (ticket) => {
    if (!ticket?.ticket_id) return;
    setLoadingMessages(true);
    try {
      const res = await axios.get(
        `${API_URL}/whatsapp/tickets/${ticket.ticket_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTicketMessages(res.data?.messages || []);
      setSelectedTicket({ ...ticket, ...res.data });
    } catch (error) {
      console.error("Error fetching ticket messages:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      await axios.post(
        `${API_URL}/whatsapp/tickets/${selectedTicket.ticket_id}/reply`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reply sent via WhatsApp");
      setReplyMessage("");
      fetchTicketMessages(selectedTicket);
      fetchTickets();
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error(error.response?.data?.detail || "Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/whatsapp/tickets/${ticketId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Ticket marked as ${newStatus}`);
      fetchTickets();
      if (selectedTicket?.ticket_id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update ticket status");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-500 text-white"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "in_progress":
        return <Badge className="bg-blue-500 text-white"><Loader2 className="w-3 h-3 mr-1" />In Progress</Badge>;
      case "resolved":
        return <Badge className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" />Resolved</Badge>;
      case "closed":
        return <Badge className="bg-gray-500 text-white"><XCircle className="w-3 h-3 mr-1" />Closed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(totalTickets / pageSize);

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const targetPage = parseInt(pageInput) - 1;
    if (targetPage >= 0 && targetPage < totalPages) {
      setPage(targetPage);
    }
    setPageInput("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar />
      <div className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <MessageSquare className="w-7 h-7 text-green-600" />
                WhatsApp Support Tickets
              </h1>
              <p className="text-slate-500 mt-1">
                Manage customer support requests from WhatsApp
              </p>
            </div>
            <Button
              onClick={fetchTickets}
              variant="outline"
              disabled={loading}
              data-testid="refresh-tickets-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Resolved</p>
                    <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Tickets</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
                  </div>
                  <MessageCircle className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by ticket ID, phone, name, or reference..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(0);
                    }}
                    className="pl-10"
                    data-testid="search-tickets-input"
                  />
                </div>
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v);
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-[180px]" data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tickets</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Tickets Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[140px]">Ticket ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : tickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                        No tickets found
                      </TableCell>
                    </TableRow>
                  ) : (
                    tickets.map((ticket) => (
                      <TableRow
                        key={ticket.ticket_id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => handleViewTicket(ticket)}
                        data-testid={`ticket-row-${ticket.ticket_id}`}
                      >
                        <TableCell className="font-mono text-sm font-medium text-blue-600">
                          {ticket.ticket_id}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{ticket.customer_name || "Unknown"}</span>
                            {ticket.customer_company && (
                              <span className="text-xs text-slate-500">{ticket.customer_company}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{ticket.phone_number}</TableCell>
                        <TableCell>
                          {ticket.reference_number && (
                            <Badge variant="outline" className="font-mono">
                              {ticket.reference_number}
                            </Badge>
                          )}
                          {ticket.related_pf && (
                            <Badge variant="outline" className="ml-1 bg-blue-50">
                              <Package className="w-3 h-3 mr-1" />
                              {ticket.related_pf}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {formatDate(ticket.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewTicket(ticket);
                            }}
                            data-testid={`view-ticket-${ticket.ticket_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <span className="text-sm text-slate-500">
                  Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalTickets)} of {totalTickets}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <form onSubmit={handlePageInputSubmit} className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      placeholder={`${page + 1}`}
                      className="w-16 h-8 text-center"
                      min={1}
                      max={totalPages}
                    />
                    <span className="text-sm text-slate-500">/ {totalPages}</span>
                  </form>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      </div>

      {/* Ticket Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              Ticket #{selectedTicket?.ticket_id}
            </DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4">
              {/* Customer Info */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Customer Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 pt-0">
                  <div>
                    <label className="text-xs text-slate-500">Name</label>
                    <p className="font-medium">{selectedTicket.customer_name || "Not provided"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Company</label>
                    <p className="font-medium">{selectedTicket.customer_company || "Not provided"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      <Phone className="w-3 h-3" />Phone
                    </label>
                    <p className="font-mono">{selectedTicket.phone_number}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" />Email
                    </label>
                    <p>{selectedTicket.customer_email || "Not provided"}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Reference Info */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Reference Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4 pt-0">
                  <div>
                    <label className="text-xs text-slate-500">Reference Number</label>
                    <p className="font-mono">{selectedTicket.reference_number || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Position Folder</label>
                    <p className="font-mono">{selectedTicket.related_pf || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedTicket.status)}</div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Created</label>
                    <p className="text-sm">{formatDate(selectedTicket.created_at)}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Conversation */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Conversation
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="bg-slate-50 rounded-lg p-4 max-h-80 overflow-y-auto space-y-2.5" data-testid="ticket-conversation">
                    {loadingMessages && ticketMessages.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Loading conversation…</p>
                    ) : ticketMessages.length === 0 ? (
                      <pre className="whitespace-pre-wrap text-sm font-mono text-slate-700">
                        {selectedTicket.chat_transcript || "No messages yet"}
                      </pre>
                    ) : (
                      ticketMessages.map((m, i) => {
                        const isCustomer = m.sender === "customer";
                        const isStaff = m.sender === "staff";
                        const label = isCustomer
                          ? (selectedTicket.customer_name || "Customer")
                          : isStaff ? `You${m.by ? " · " + m.by : ""}` : "Roket Bot";
                        return (
                          <div key={i} className={`flex ${isCustomer ? "justify-start" : "justify-end"}`} data-testid={`msg-${m.sender}-${i}`}>
                            <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                              isCustomer
                                ? "bg-white text-slate-800 rounded-bl-sm border border-slate-200"
                                : isStaff
                                  ? "bg-emerald-600 text-white rounded-br-sm"
                                  : "bg-blue-100 text-slate-800 rounded-br-sm"
                            }`}>
                              <div className={`text-[10px] font-semibold mb-0.5 ${isStaff ? "text-emerald-50" : "text-slate-400"}`}>
                                {label}
                              </div>
                              <div className="whitespace-pre-wrap break-words">{m.text}</div>
                              {m.timestamp && (
                                <div className={`text-[9px] mt-1 ${isStaff ? "text-emerald-100" : "text-slate-400"}`}>
                                  {formatDate(m.timestamp)}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Reply Section */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Send Reply via WhatsApp
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <Textarea
                    placeholder="Type your reply message..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={3}
                    data-testid="reply-message-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSendReply}
                      disabled={!replyMessage.trim() || sendingReply}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="send-reply-btn"
                    >
                      {sendingReply ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send WhatsApp Reply
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Status Actions */}
              <div className="flex gap-2 pt-2">
                {selectedTicket.status === "pending" && (
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus(selectedTicket.ticket_id, "in_progress")}
                    className="text-blue-600 border-blue-200"
                  >
                    <Loader2 className="w-4 h-4 mr-2" />
                    Mark In Progress
                  </Button>
                )}
                {selectedTicket.status !== "resolved" && (
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus(selectedTicket.ticket_id, "resolved")}
                    className="text-green-600 border-green-200"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Resolved
                  </Button>
                )}
                {selectedTicket.status !== "closed" && (
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus(selectedTicket.ticket_id, "closed")}
                    className="text-slate-600 border-slate-200"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Close Ticket
                  </Button>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
